import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { apiRequest } from "../../api/http";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchEmployeeDetail } from "../../store/slices/employeesSlice";
import { EmployeePayrollResponse, EmployeePayrollStatement } from "../../types/admin";
import { formatDate } from "../../utils/admin";
import { getPayrollTitle, getPayrollTypeLabel, isPayrollFileOnly } from "../../utils/payroll";

const EmployeePayrollsPage = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { detail, loadingDetail, detailError } = useAppSelector((state) => state.employees);

  const [payrolls, setPayrolls] = useState<EmployeePayrollStatement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenPayroll = (payroll: EmployeePayrollStatement) => {
    navigate(`/admin/employees/${employeeId}/payrolls/${payroll.payroll_id}`);
  };

  useEffect(() => {
    if (employeeId && (!detail || detail.employee_id !== Number(employeeId))) {
      dispatch(fetchEmployeeDetail(Number(employeeId)));
    }
  }, [dispatch, employeeId, detail]);

  useEffect(() => {
    const fetchDocs = async () => {
      if (!detail?.branch_id || !employeeId) return;
      
      setLoading(true);
      setError(null);
      try {
        const res = await apiRequest<EmployeePayrollResponse>(`/admin/branches/${detail.branch_id}/employees/${employeeId}/payrolls`);
        setPayrolls(res.items);
      } catch (err: any) {
        setError(err.message || "급여명세서 목록을 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    if (detail) {
      fetchDocs();
    }
  }, [detail, employeeId]);

  return (
    <section className="admin-page">
      <header className="detail-header">
        <button className="back-btn" type="button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h2 className="section-title">
          급여명세서 목록 {detail ? `(${detail.employee_name})` : ''}
        </h2>
      </header>

      {loadingDetail && <p className="loading-text">직원 정보를 불러오는 중입니다...</p>}
      {detailError && <p className="error-text">{detailError}</p>}

      {!loadingDetail && !detailError && (
        <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '16px', marginTop: '24px' }}>
          {loading && <p>데이터를 불러오는 중입니다...</p>}
          {error && <p className="error-text">{error}</p>}
          
          {!loading && !error && (
            <div className="table-shell" style={{ overflowX: 'auto' }}>
              <table className="admin-table compact" style={{ whiteSpace: 'nowrap' }}>
                <thead>
                  <tr>
                    <th>급여명세서</th>
                    <th>등록유형</th>
                    <th>지급 총액</th>
                    <th>공제 총액</th>
                    <th>실 수령액</th>
                    <th>생성일</th>
                    <th style={{ textAlign: 'center' }}>상세</th>
                  </tr>
                </thead>
                <tbody>
                  {payrolls.map(payroll => (
                    <tr key={payroll.payroll_id}>
                      <td>{getPayrollTitle(payroll)}</td>
                      <td>
                        <span className={`branch-status-badge ${isPayrollFileOnly(payroll) ? 'branch-status-badge--neutral' : 'branch-status-badge--operating'}`}>
                          {getPayrollTypeLabel(payroll)}
                        </span>
                      </td>
                      <td>{payroll.gross_salary.toLocaleString()}원</td>
                      <td>{payroll.total_deduction.toLocaleString()}원</td>
                      <td>{payroll.net_pay.toLocaleString()}원</td>
                      <td>{formatDate(payroll.created_at)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="detail-icon-btn"
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            color: '#1d1d1f',
                            border: '1px solid #e7e8ef',
                            borderRadius: '6px',
                            backgroundColor: 'white',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleOpenPayroll(payroll)}
                        >
                          {isPayrollFileOnly(payroll) ? "파일보기" : "상세보기"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {payrolls.length === 0 && (
                    <tr>
                      <td colSpan={7} className="empty-row">등록된 급여명세서가 없습니다.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default EmployeePayrollsPage;
